/**
 * MemoryModal - A beautiful modal for creating memories
 * Works on both desktop (centered modal) and mobile (bottom sheet)
 */

import React, { useState, useRef, useEffect } from 'react';
import { logger } from '../../utils/logger';
import './MemoryModal.css';

// Post-it colors matching our palette
const POST_IT_COLORS = [
  { name: 'Dusty Rose', hex: '#E6B3C2' },
  { name: 'Peach', hex: '#F2C6A0' },
  { name: 'Sage', hex: '#C9D3B8' },
  { name: 'Paper', hex: '#F4EDE4' },
  { name: 'Warm', hex: '#D6CEC4' },
];

const MAX_CHARS = 280;

export default function MemoryModal({
  isOpen,
  onClose,
  onSubmit,
  location,
  loading = false,
  user = null,
  onOpenAuth = null
}) {
  const [text, setText] = useState('');
  const [selectedColor, setSelectedColor] = useState(POST_IT_COLORS[0].hex);
  const isAnonymous = !user;
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);
  const modalRef = useRef(null);

  // Focus textarea when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim() || loading) return;

    setError(null);
    try {
      await onSubmit({
        text: text.trim(),
        lat: location.lat,
        lng: location.lng,
        color: selectedColor,
        isAnonymous,
      });
      setText('');
      setSelectedColor(POST_IT_COLORS[0].hex);
      onClose();
    } catch (err) {
      logger.error('Failed to submit:', err);
      setError(err.message || 'Failed to create memory. Please try again.');
    }
  };

  const charsRemaining = MAX_CHARS - text.length;
  const isOverLimit = charsRemaining < 0;

  if (!isOpen) return null;

  return (
    <div 
      ref={modalRef}
      className="memory-modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="memory-modal-title"
    >
      <div className="memory-modal">
        {/* Header */}
        <div className="memory-modal-header">
          <div className="header-content">
            <span className="header-icon">✨</span>
            <h2 id="memory-modal-title">Leave a Memory</h2>
          </div>
          <button 
            className="close-button" 
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Location preview */}
        <div className="location-preview">
          <span className="location-icon">{location.icon || '📍'}</span>
          <div className="location-text">
            {location.name ? (
              <>
                <span className="location-name">{location.name}</span>
                {location.city && (
                  <span className="location-city">{location.city}, {location.country}</span>
                )}
              </>
            ) : (
              <span className="location-coords">
                {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
              </span>
            )}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Post-it preview with textarea */}
          <div 
            className="post-it-preview"
            style={{ backgroundColor: selectedColor }}
          >
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What memory would you like to leave here?"
              maxLength={MAX_CHARS + 20} // Allow slight overflow for UX
              rows={4}
              className="memory-textarea"
            />
            <div className={`char-counter ${isOverLimit ? 'over-limit' : charsRemaining < 30 ? 'warning' : ''}`}>
              {charsRemaining}
            </div>
          </div>

          {/* Color picker */}
          <div className="color-picker">
            <span className="picker-label">Pick a color:</span>
            <div className="color-options">
              {POST_IT_COLORS.map((color) => (
                <button
                  key={color.hex}
                  type="button"
                  className={`color-option ${selectedColor === color.hex ? 'selected' : ''}`}
                  style={{ backgroundColor: color.hex }}
                  onClick={() => setSelectedColor(color.hex)}
                  title={color.name}
                  aria-label={`Select ${color.name} color`}
                />
              ))}
            </div>
          </div>

          {/* Auth status / Anonymous toggle */}
          {user ? (
            <div className="auth-status">
              <div className="auth-status-info">
                <span className="auth-status-icon">✅</span>
                <div>
                  <span className="auth-status-text">Signed in as {user.displayName || user.email?.split('@')[0] || 'User'}</span>
                  <p className="auth-status-hint">Your memory will be saved permanently</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="anonymous-warning-banner">
              <div className="warning-content">
                <span className="warning-icon">&#8987;</span>
                <div className="warning-text">
                  <span className="warning-title">Anonymous memories fade away</span>
                  <span className="warning-detail">
                    This note will disappear in 7 days.{' '}
                    <button
                      type="button"
                      className="sign-in-link"
                      onClick={onOpenAuth}
                    >
                      Sign in to keep it forever
                    </button>
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div style={{
              backgroundColor: '#FEE2E2',
              color: '#991B1B',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              fontSize: '0.85rem',
              marginBottom: '0.75rem',
            }}>
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="modal-actions">
            <button
              type="button"
              className="action-button secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="action-button primary"
              disabled={!text.trim() || isOverLimit || loading}
            >
              {loading ? (
                <span className="loading-spinner">⏳</span>
              ) : (
                <>
                  <span>✨</span>
                  <span>Leave Memory</span>
                </>
              )}
            </button>
          </div>
        </form>

        {/* Fun tip */}
        <p className="modal-tip">
          💡 Tip: Your memory will appear as a post-it note on the map for others to discover!
        </p>
      </div>
    </div>
  );
}

